import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader2, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { useAuth } from "@/lib/hooks/useAuth";
import { JobboardCampaignType } from "@/types/response/CampaignJobBoardMonthType";
import { UserRole } from "@/types/user/userType";
import { formatCurrency } from "@/utils/valueFormat";

interface SpentRowProps {
  existingData: JobboardCampaignType;
  isEditing: boolean;
  isEditingBudget: boolean;
  editValue: string;
  isLoading: boolean;
  hasBudget: boolean;
  canEditSpent: boolean;
  canCreateSpent: boolean;
  isFutureMonth: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onValueChange: (value: string) => void;
  getNumberValue: (value: string) => string;
}

type SpentFormValues = {
  spent: string;
};

function SpentRow({
  existingData,
  isEditing,
  isEditingBudget,
  editValue,
  isLoading,
  hasBudget,
  canEditSpent,
  canCreateSpent,
  isFutureMonth,
  onEdit,
  onSave,
  onCancel,
  onValueChange,
  getNumberValue,
}: SpentRowProps) {
  const { t } = useTranslation();
  const { role } = useAuth();

  const MAX_SPENT = 9999999;

  const canViewMarginBreakdown =
    (role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN) &&
    existingData.includeMargin;
  const hasSpent =
    existingData.actualSpent != null ||
    (canViewMarginBreakdown && existingData.spent != null);
  const isAddingNewSpent = isEditing && hasBudget && !hasSpent;

  // Create validation schema with max spent of 9999999
  const spentSchema = useMemo(() => {
    return z.object({
      spent: z
        .string()
        .refine(
          (val) => {
            if (val.trim() === "") return true; // Allow empty for optional spent
            const numValue = Number.parseFloat(val);
            return !Number.isNaN(numValue) && numValue >= 0;
          },
          {
            message: t("validation.invalidAmount", {
              defaultValue: "Invalid spent amount",
            }),
          }
        )
        .refine(
          (val) => {
            if (val.trim() === "") return true; // No limit if empty
            const numValue = Number.parseFloat(val);
            return numValue <= MAX_SPENT;
          },
          {
            message: t("errors.budgetMax", {
              fieldName: t("common.spent"),
              max: MAX_SPENT.toLocaleString(),
            }),
          }
        ),
    });
  }, [t]);

  const form = useForm<SpentFormValues>({
    resolver: zodResolver(spentSchema),
    defaultValues: {
      spent: editValue,
    },
    mode: "onChange",
  });

  // Sync form value with external editValue prop
  useEffect(() => {
    if (isEditing) {
      form.setValue("spent", editValue, { shouldValidate: true });
    }
  }, [editValue, isEditing, form]);

  // Reset form when editing state changes
  useEffect(() => {
    if (!isEditing) {
      form.reset({ spent: existingData.actualSpent?.toString() ?? "" });
    }
  }, [isEditing, existingData.actualSpent, form]);

  const handleSave = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();
    onValueChange(formData.spent);
    onSave();
  };

  const handleCancel = () => {
    form.reset({ spent: existingData.actualSpent?.toString() ?? "" });
    onCancel();
  };

  const handleValueChange = (value: string) => {
    const processedValue = getNumberValue(value);
    form.setValue("spent", processedValue, { shouldValidate: true });
    onValueChange(processedValue);
  };

  return (
    <div className="group/spent-row min-h-9">
      <AnimatePresence mode="wait">
        {isEditing ? (
          <motion.div
            key="edit"
            initial={{ y: 20, opacity: 0, height: 0 }}
            animate={{ y: 0, opacity: 1, height: "auto" }}
            exit={{ y: 20, opacity: 0, height: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSave)}
                className="space-y-2"
              >
                <FormField
                  control={form.control}
                  name="spent"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-foreground">
                            {t("common.editSpent")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <FormattedCurrencyInput
                              value={
                                field.value
                                  ? Number.parseFloat(field.value)
                                  : undefined
                              }
                              variant={"ghost"}
                              suffix={false}
                              caretPosition={"right"}
                              size={"sm"}
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min={0}
                              max={MAX_SPENT}
                              onChange={(value) => {
                                const processedValue =
                                  value !== undefined ? value.toString() : "";
                                field.onChange(processedValue);
                                handleValueChange(processedValue);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleSave();
                                } else if (e.key === "Escape") {
                                  handleCancel();
                                }
                              }}
                              className="h-9 w-32 text-base font-semibold"
                              placeholder="0.00"
                              disabled={isLoading}
                              autoFocus
                            />
                          </FormControl>
                          <div className="flex items-center gap-1">
                            <Button
                              type="submit"
                              variant="ghost"
                              size="sm"
                              disabled={isLoading || !form.formState.isValid}
                              className="h-9 w-9 p-0 hover-tint-primary hover:text-primary"
                              title={
                                isAddingNewSpent
                                  ? t("common.create")
                                  : t("common.save")
                              }
                            >
                              {isLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleCancel}
                              disabled={isLoading}
                              className="h-9 w-9 p-0 hover:bg-destructive/10 hover:text-destructive"
                              title={t("common.cancel")}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            className="flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground">
                {t("common.spent")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasBudget &&
              !isFutureMonth &&
              !isEditingBudget &&
              (hasSpent ? canEditSpent : canCreateSpent) ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex flex-col gap-0.5 items-end cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={onEdit}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(); } }}
                      >
                        {hasSpent ? (
                          <>
                            {canViewMarginBreakdown &&
                          existingData.spent != null ? (
                                <>
                                  {existingData.actualSpent != null && (
                                    <span
                                      className="text-lg font-semibold tracking-tight transition-all hover:spent-row:underline"
                                      title={t("common.withoutMargin")}
                                    >
                                      {formatCurrency(
                                        existingData.actualSpent)}
                                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                                        {t("common.woMargin")}
                                      </span>
                                    </span>
                                  )}
                                  <span className="text-xs text-muted-foreground/70">
                                    {formatCurrency(existingData.spent)}
                                    <span
                                      className="ml-1"
                                      title={t("common.withMargin")}
                                    >
                                      {t("common.marginAbbr")}
                                    </span>
                                  </span>
                                </>
                              ) : (
                                <span className="text-lg font-semibold tracking-tight hover:underline">
                                  {formatCurrency(existingData.actualSpent!)}
                                </span>
                              )}
                          </>
                        ) : (
                          <span className="text-lg font-medium hover:underline">
                            {formatCurrency(0)}
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {hasSpent ? t("common.editSpent") : t("common.addSpent")}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="flex flex-col gap-0.5 items-end">
                    {hasSpent ? (
                      <>
                        {canViewMarginBreakdown && existingData.spent != null ? (
                          <>
                            {existingData.actualSpent != null && (
                              <span
                                className="text-lg font-semibold tracking-tight hover:underline"
                                title={t("common.withoutMargin")}
                              >
                                {formatCurrency(existingData.actualSpent)}
                                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                                  {t("common.woMargin")}
                                </span>
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground/70">
                              {formatCurrency(existingData.spent)}
                              <span
                                className="ml-1"
                                title={t("common.withMargin")}
                              >
                                {t("common.marginAbbr")}
                              </span>
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-semibold tracking-tight">
                            {formatCurrency(existingData.actualSpent!)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-lg font-medium">
                        {formatCurrency(0)}
                      </span>
                    )}
                  </div>
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SpentRow;
